import type { AiCrawlerLineup, AiCrawlerProvider } from '../shared';
import { mapNormalizedToProvider } from '../shared';
import type { DiscoverabilityChartResponse } from './api/discoverabilityChartsApi';

// Bucket for response crawler ids that map to no tracked provider. Unreachable
// today (the chart endpoints only return whitelisted, provider-mapped ids) but
// handled defensively here and by consumers that partition ids by provider.
export const OTHER_PROVIDER = 'Other' as const;

export type ChartSeriesProvider = AiCrawlerProvider | typeof OTHER_PROVIDER;

export type ChartSeries = {
  dates: string[];
  perCrawler: Array<{ provider: ChartSeriesProvider; values: number[] }>;
  allCrawlers: number[];
};

function emptyValues(length: number): number[] {
  return Array.from({ length }, () => 0);
}

export default function parseChartResponseData(
  lineup: AiCrawlerLineup,
  response: DiscoverabilityChartResponse
): ChartSeries {
  const dates = response.data.map((point) => point.date);
  const length = dates.length;

  const providerSeries: Map<AiCrawlerProvider, number[]> = new Map();
  lineup.providers.forEach((provider) => {
    providerSeries.set(provider, emptyValues(length));
  });
  const otherSeries = emptyValues(length);
  let otherHasData = false;

  const activeProviders = new Set(
    ('activeCrawlers' in response ? response.activeCrawlers : [])
      .map((crawler) => mapNormalizedToProvider(lineup, crawler))
      .filter((provider): provider is AiCrawlerProvider => provider !== undefined)
  );

  response.data.forEach((point, dayIndex) => {
    Object.entries(point.crawlers).forEach(([key, value]) => {
      const provider = mapNormalizedToProvider(lineup, key);
      if (provider) {
        const series = providerSeries.get(provider);
        // Summing here is only valid for count-valued series (Crawl Requests).
        // Percentage-valued series (Error Rate) must never be summed across
        // sub-labels; the backend guarantees error-rate responses carry one
        // pre-blended canonical key per provider (PRE-3145).
        if (series) series[dayIndex] += value;
      } else {
        otherSeries[dayIndex] += value;
        if (value !== 0) otherHasData = true;
      }
    });
  });

  const perCrawler: ChartSeries['perCrawler'] = [
    ...lineup.providers
      .map((provider) => ({ provider, values: providerSeries.get(provider) ?? emptyValues(length) }))
      .filter(({ provider, values }) => values.some((v) => v !== 0) || activeProviders.has(provider)),
    ...(otherHasData ? [{ provider: OTHER_PROVIDER, values: otherSeries }] : []),
  ];

  const allCrawlers = response.data.map((point) => point.all);

  return { dates, perCrawler, allCrawlers };
}
